import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  dashboardsApi,
  type DashboardCatalog,
  type DashboardWidgetItem,
  type WidgetPeriod,
  type WidgetSize,
} from '../api/dashboards'
import { savedViewsApi, type SavedView } from '../api/savedViews'
import { WIDGET_COMPONENTS } from '../components/dashboardWidgets'
import { Button, Callout, Card, CardContent, CardGrid, CardGridItem, CardHeader, EmptyState, IconButton, LoadingState, Page, PageHeader, Select, useConfirm } from '../components/ui'

/**
 * F-072 (REQ-271-001) — la mia dashboard: widget del catalogo, ordine, dimensione, filtri per widget e "Ripristina
 * base". Il server decide quali widget spettano al ruolo e quanti se ne possono avere; qui si lavora su una bozza e
 * si salva con un solo PUT. Senza layout salvato si vede il base del ruolo.
 */

const SIZE_SPAN: Record<WidgetSize, 1 | 2 | 'full'> = { s: 1, m: 2, l: 'full' }
const SIZE_LABEL: Record<WidgetSize, string> = { s: 'Piccolo', m: 'Medio', l: 'Grande' }
const PERIOD_LABEL: Record<WidgetPeriod, string> = {
  '7d': 'Ultimi 7 giorni',
  '30d': 'Ultimi 30 giorni',
  '90d': 'Ultimi 90 giorni',
  thisMonth: 'Mese in corso',
}

function itemId(w: DashboardWidgetItem): string {
  return w.key === 'saved-view' ? `saved-view:${w.savedViewId}` : w.key
}

export function MyDashboardPage() {
  const confirm = useConfirm()
  const [catalog, setCatalog] = useState<DashboardCatalog | null>(null)
  const [widgets, setWidgets] = useState<DashboardWidgetItem[]>([])
  const [isDefault, setIsDefault] = useState(true)
  const [removedKeys, setRemovedKeys] = useState<string[]>([])
  const [dirty, setDirty] = useState(false)
  const [editing, setEditing] = useState(false)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [views, setViews] = useState<SavedView[]>([])
  const [pick, setPick] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [cat, mine] = await Promise.all([dashboardsApi.catalog(), dashboardsApi.mine()])
      setCatalog(cat)
      setWidgets(mine.widgets)
      setIsDefault(mine.isDefault)
      setRemovedKeys(mine.removedKeys)
      setDirty(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (!editing || !catalog?.widgets.some(w => w.needsSavedView)) return
    Promise.all([savedViewsApi.list('contracts'), savedViewsApi.list('leads')])
      .then(([c, l]) => setViews([...c.items, ...l.items]))
      .catch(() => setViews([]))
  }, [editing, catalog])

  const byKey = useMemo(() => new Map((catalog?.widgets ?? []).map(w => [w.key, w])), [catalog])
  const max = catalog?.maxWidgets ?? 0

  const addable = useMemo(() => {
    const present = new Set(widgets.map(itemId))
    const options: { value: string; label: string }[] = []
    for (const w of catalog?.widgets ?? []) {
      if (w.needsSavedView) {
        for (const v of views)
          if (!present.has(`saved-view:${v.id}`)) options.push({ value: `saved-view:${v.id}`, label: `Vista salvata: ${v.name}` })
      } else if (!present.has(w.key)) {
        options.push({ value: w.key, label: w.label })
      }
    }
    return options
  }, [catalog, widgets, views])

  function change(next: DashboardWidgetItem[]) {
    setWidgets(next)
    setDirty(true)
  }

  function add() {
    if (!pick) return
    const [key, rest] = pick.split(':')
    const def = byKey.get(key)
    if (!def) return
    change([...widgets, { key, size: def.defaultSize, filters: null, savedViewId: rest ? Number(rest) : null }])
    setPick('')
  }

  function move(index: number, delta: number) {
    const target = index + delta
    if (target < 0 || target >= widgets.length) return
    const next = [...widgets]
    ;[next[index], next[target]] = [next[target]!, next[index]!]
    change(next)
  }

  function patch(index: number, p: Partial<DashboardWidgetItem>) {
    change(widgets.map((w, i) => (i === index ? { ...w, ...p } : w)))
  }

  async function save() {
    setSaving(true)
    setError('')
    try {
      const res = await dashboardsApi.save(widgets)
      setWidgets(res.widgets)
      setIsDefault(res.isDefault)
      setRemovedKeys(res.removedKeys)
      setDirty(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore')
    } finally {
      setSaving(false)
    }
  }

  async function reset() {
    const ok = await confirm({
      title: 'Ripristinare la dashboard di base',
      message: 'Ripristinare la dashboard di base del tuo ruolo? Il layout salvato verrà eliminato.',
      confirmLabel: 'Ripristina',
      destructive: true,
    })
    if (!ok) return
    setError('')
    try {
      await dashboardsApi.resetToBase()
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore')
    }
  }

  async function toggleEditing() {
    if (editing && dirty) {
      const ok = await confirm({
        title: 'Uscire senza salvare',
        message: 'Ci sono modifiche non salvate. Uscire comunque?',
        confirmLabel: 'Esci',
      })
      if (!ok) return
      void load()
    }
    setEditing(!editing)
  }

  return (
    <Page>
      <PageHeader
        title="La mia dashboard"
        icon="space_dashboard"
        subtitle={`${isDefault ? 'Layout di base del tuo ruolo.' : 'Layout personale.'} Massimo ${max} widget.`}
        actions={
          <>
            {editing && (
              <Button type="button" variant="outlined" onClick={() => void reset()}>
                Ripristina base
              </Button>
            )}
            <Button
              type="button"
              variant="outlined"
              onClick={() => void toggleEditing()}
            >
              {editing ? 'Fine' : 'Personalizza'}
            </Button>
            {editing && (
              <Button type="button" variant="filled" disabled={!dirty || saving} onClick={() => void save()}>
                {saving ? 'Salvataggio...' : 'Salva'}
              </Button>
            )}
          </>
        }
      />

      {error && <Callout tone="error">{error}</Callout>}
      {removedKeys.length > 0 && (
        <Callout tone="warning">
          Alcuni widget salvati non sono più ammessi al tuo ruolo e non vengono mostrati: {removedKeys.join(', ')}.
        </Callout>
      )}

      {editing && (
        <Card>
          <div className="d-flex flex-wrap align-items-center gap-2">
            <div className="md-row-field">
              <Select label="Aggiungi un widget" value={pick} onChange={e => setPick(e.target.value)}>
                <option value="">Scegli...</option>
                {addable.map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Button type="button" variant="tonal" disabled={!pick || widgets.length >= max} onClick={add}>
                Aggiungi
              </Button>
            </div>
            <div className="text-muted md-typescale-body-small">
              {widgets.length} / {max}
            </div>
          </div>
        </Card>
      )}

      {loading && <LoadingState />}

      {!loading && widgets.length === 0 && <EmptyState icon="dashboard" title="Nessun widget. Usa «Personalizza» per aggiungerne." />}

      <CardGrid min={240} max={4}>
        {widgets.map((w, index) => {
          const def = byKey.get(w.key)
          const Component = WIDGET_COMPONENTS[w.key]
          if (!def || !Component) return null
          return (
            <CardGridItem span={SIZE_SPAN[w.size]} key={itemId(w)}>
              <Card className="h-100">
                <CardHeader
                  title={def.label}
                  headingLevel={3}
                  actions={
                    editing ? (
                      <div className="d-flex gap-1 align-items-center">
                        {def.allowsFilters && (
                          <Select
                            label="Periodo"
                            labelHidden
                            value={w.filters?.period ?? ''}
                            onChange={e =>
                              patch(index, { filters: e.target.value ? { ...w.filters, period: e.target.value as WidgetPeriod } : null })
                            }
                          >
                            <option value="">Periodo predefinito</option>
                            {(catalog?.periods ?? []).map(p => (
                              <option key={p} value={p}>
                                {PERIOD_LABEL[p]}
                              </option>
                            ))}
                          </Select>
                        )}
                        <Select
                          label="Dimensione"
                          labelHidden
                          value={w.size}
                          onChange={e => patch(index, { size: e.target.value as WidgetSize })}
                        >
                          {def.sizes.map(s => (
                            <option key={s} value={s}>
                              {SIZE_LABEL[s]}
                            </option>
                          ))}
                        </Select>
                        <IconButton icon="arrow_upward" aria-label="Sposta su" size="sm" disabled={index === 0} onClick={() => move(index, -1)} />
                        <IconButton
                          icon="arrow_downward"
                          aria-label="Sposta giù"
                          size="sm"
                          disabled={index === widgets.length - 1}
                          onClick={() => move(index, 1)}
                        />
                        <IconButton icon="close" aria-label="Rimuovi" size="sm" onClick={() => change(widgets.filter((_, i) => i !== index))} />
                      </div>
                    ) : undefined
                  }
                />
                <CardContent>
                  <Component item={w} />
                </CardContent>
              </Card>
            </CardGridItem>
          )
        })}
      </CardGrid>
    </Page>
  )
}
